import { get, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormSection, RepeatableList } from "@/features/admin/components";
import { PackageItemRow } from "./PackageItemRow";
import type { PackagePreviewState } from "../usePackagePreview";
import type { PackageFormValues } from "../packageForm";

export interface PackageItemsSectionProps {
  state: PackagePreviewState;
}

/**
 * Los productos que lleva el paquete.
 *
 * Es la lista de la que sale TODO lo demás: el precio, los VP y hasta el
 * envío. Por eso está antes del descuento en el formulario — escribir un
 * porcentaje sobre una lista vacía es escribir un porcentaje de nada.
 *
 * `RepeatableList` es la misma lista dinámica de los beneficios y los
 * ingredientes del producto: `useFieldArray` por debajo (para que cada
 * fila tenga una `key` estable y borrar la fila 2 no corra los valores
 * hacia arriba), botones de subir y bajar en vez de arrastrar, y cada
 * botón diciendo sobre qué fila actúa.
 *
 * Lo único que le faltaba era dónde pintar el error de la LISTA —"un
 * paquete necesita al menos un producto", que el contrato pone sobre el
 * array y no sobre ningún campo—; se le añadió un `error` opcional que
 * no cambia nada para quien no lo pasa.
 */
export function PackageItemsSection({ state }: PackageItemsSectionProps) {
  const { t } = useTranslation();
  const { formState } = useFormContext<PackageFormValues>();

  /*
   * El error de la LISTA vive en `items.root`, no en `items`.
   *
   * react-hook-form reserva `errors.items` para los errores POR FILA
   * —`items.0.productId`, `items.1.quantity`— y mete el del conjunto un
   * nivel más abajo, en `root`. Leerlo como `errors.items.message` no
   * falla ni avisa: devuelve `undefined`, la lista se queda sin error a
   * la vista, y "Guardar" no hace nada sin decir por qué. Se cae en ello
   * una vez y solo se descubre imprimiendo el objeto de errores.
   *
   * Se leen los dos por si un resolver lo coloca en la ruta llana.
   */
  const arrayError: unknown =
    get(formState.errors, "items.root")?.message ??
    get(formState.errors, "items")?.message;

  return (
    <FormSection
      title={t("Productos del paquete")}
      description={t(
        "Elige qué lleva y cuántos de cada uno. De aquí salen el precio y los VP: el paquete no tiene precio propio.",
      )}
      columns={1}
    >
      <RepeatableList
        name="items"
        addLabel={t("Añadir producto")}
        itemLabel={(index) => t("Producto {{n}}", { n: index + 1 })}
        newItem={() => ({ productId: "", quantity: "1" })}
        error={typeof arrayError === "string" ? arrayError : undefined}
        // La pista de la lista vacía dice qué HACER. El "un paquete
        // necesita al menos un producto" es el error del contrato y llega
        // por `error`; repetirlo aquí dejaría el mismo texto dos veces en
        // pantalla y ninguna de las dos veces sabría si es un aviso o una
        // instrucción.
        emptyHint={t("Todavía no hay productos. Añade el primero.")}
      >
        {({ name }) => (
          <PackageItemRow
            name={name}
            products={state.products}
            catalog={state.catalog}
            loading={state.loading}
          />
        )}
      </RepeatableList>
    </FormSection>
  );
}
